import React, { useState, useEffect, useCallback, useRef } from "react";

import { DialogContent } from "@mui/material";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  IconButton,
  Popover
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

import SupportModal from "../../container/SupportModal/Modal";
import Modal from "../../HOC/modal/Modal";
import esFlag from "../../assets/images/flag-es.svg";
import enFlag from "../../assets/images/flag-uk.svg";
import pgFlag from "../../assets/images/flag-pg.svg";
import { ReactComponent as CasinoIcon } from "../../assets/images/Casino.svg";
import { ReactComponent as LobbyIcon } from "../../assets/images/Lobby_icon.svg";
import { ReactComponent as Favorites } from "../../assets/images/Favorites.svg";
import { ReactComponent as Providers } from "../../assets/images/provider.svg";
import { ReactComponent as OnFire } from "../../assets/images/OnFire.svg";
import { ReactComponent as NewGames } from "../../assets/images/NewGames.svg";
import { ReactComponent as LiveCasinoMob } from "../../assets/images/LiveCasinoMob.svg";
import { ReactComponent as Slots } from "../../assets/images/Slots.svg";
import { ReactComponent as Poker } from "../../assets/images/Poker.svg";
import { ReactComponent as Baccarat } from "../../assets/images/Baccarat.svg";
import { ReactComponent as Roulette } from "../../assets/images/Roulette.svg";
import { ReactComponent as Sports } from "../../assets/images/SportsIcon.svg";
import { ReactComponent as Football } from "../../assets/images/Football.svg";
import { ReactComponent as Basketball } from "../../assets/images/Basketball.svg";
import { ReactComponent as Tennis } from "../../assets/images/Tennis.svg";
import { ReactComponent as AmericanFootball } from "../../assets/images/American_Football.svg";
import { ReactComponent as Formula1 } from "../../assets/images/Formula_1.svg";
import { ReactComponent as Baseball } from "../../assets/images/Baseball.svg";
import { ReactComponent as EFootball } from "../../assets/images/EFootball.svg";
import { ReactComponent as MMA } from "../../assets/images/MMA.svg";
import { ReactComponent as Bonus } from "../../assets/images/Bonus.svg";
// import { ReactComponent as Blog } from "../../assets/images/Blog.svg"
// import { ReactComponent as ResponsibleGaming } from "../../assets/images/Responsible_Gaming.svg"
import { ReactComponent as Support } from "../../assets/images/Support.svg";
import { ReactComponent as Frame } from "../../assets/images/Frame 37.svg";
import { ReactComponent as ArrowIcon } from "../../assets/images/ArrowToggleIcon.svg";
import { ReactComponent as Language } from "../../assets/images/language.svg";

// helper to resolve localized routes (supports string or { en, es } objects)
const resolveTo = (to) => {
  if (!to) return undefined;
  if (typeof to === "string") return to;
  return to[i18n.language] || to.en || Object.values(to)[0];
};

const ArrowToggleIcon = ({ expanded }) => (
  <ArrowIcon
    style={{
      width: 40,
      height: 40,
      // transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
      transformOrigin: "50% 50%"
    }}
  />
);

const linkStyles = ({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  textDecoration: "none",
  color: isActive ? "#FF014D" : "#949494",
  width: "100%"
});

const getIsTablet = () =>
  typeof window !== "undefined" &&
  window.innerWidth <= 1024 &&
  window.innerWidth > 600;

function Sidebar({ expanded, setExpanded }) {
  const { t } = useTranslation();
  const location = useLocation();

  const [openCasino, setOpenCasino] = useState(false);
  const [openSports, setOpenSports] = useState(false);
  const [isTablet, setIsTablet] = useState(getIsTablet());

  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [submenuType, setSubmenuType] = useState("");

  const [langAnchor, setLangAnchor] = useState(null);
  const [langHoverOpen, setLangHoverOpen] = useState(false);

  const casinoIconRef = useRef(null);
  const sportsIconRef = useRef(null);
  const langItemRef = useRef(null);

  const closeTimerRef = useRef(null);
  const lastAnchorRef = useRef(null);
  const openTimerRef = useRef(null);
  const langOpenTimerRef = useRef(null);

  const [openSupportModal, setOpenSupportModal] = useState(false);
  const handleOpenSupportModal = useCallback(() => {
    setOpenSupportModal((s) => !s);
  }, []);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 600;

  useEffect(() => {
    const handleResize = () => {
      const tablet = getIsTablet();
      setIsTablet(tablet);
      if (tablet) setExpanded(false);
      else setExpanded(true);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [setExpanded]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (langOpenTimerRef.current) clearTimeout(langOpenTimerRef.current);
      setSubmenuType("");
      setSubmenuAnchor(null);
      setLangHoverOpen(false);
      setLangAnchor(null);
    };
  }, []);

  useEffect(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (langOpenTimerRef.current) clearTimeout(langOpenTimerRef.current);
    setSubmenuType("");
    setSubmenuAnchor(null);
    setLangHoverOpen(false);
    setLangAnchor(null);
  }, [location]);

  if (isMobile) return null;

  const drawerWidth = expanded
    ? typeof window !== "undefined" && window.innerWidth < 1480
      ? 195
      : 215
    : 64;

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (langOpenTimerRef.current) {
      clearTimeout(langOpenTimerRef.current);
      langOpenTimerRef.current = null;
    }
  };

  const closeSubmenuNow = () => {
    cancelClose();
    setSubmenuType("");
    setSubmenuAnchor(null);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      closeSubmenuNow();
    }, 180);
  };

  const handleSubmenuOpen = (type, anchorEl) => {
    cancelClose();
    setSubmenuType(type);
    setSubmenuAnchor(anchorEl);
    lastAnchorRef.current = anchorEl;
  };

  const handleSubmenuClose = () => {
    closeSubmenuNow();
  };

  const handleLangClose = () => {
    setLangAnchor(null);
  };

  const handleLangChange = (lng) => {
    i18n.changeLanguage(lng);
    setLangHoverOpen(false);
    setLangAnchor(null);
  };

  const casinoItems = [
    {
      label: t("NEW_SIDEBAR.LOBBY"),
      icon: <LobbyIcon />,
      to: "/casino/casino-lobby"
    },
    {
      label: t("NEW_SIDEBAR.FAVORITES"),
      icon: <Favorites />,
      to: "/casino/casino_favorites"
    },
    {
      label: t("NEW_SIDEBAR.PROVIDERS"),
      icon: <Providers />,
      to: "/casino/casinoProvider"
    },
    {
      label: t("NEW_SIDEBAR.ON_FIRE"),
      icon: <OnFire />,
      to: "/casino/on-fire"
    },
    {
      label: t("NEW_SIDEBAR.NEW_GAMES"),
      icon: <NewGames />,
      to: "/casino/nuevos-juegos"
    },
    {
      label: t("NEW_SIDEBAR.LIVE_CASINO"),
      icon: <LiveCasinoMob />,
      to: "/casino/casinoLive"
    },
    {
      label: t("NEW_SIDEBAR.SLOTS"),
      icon: <Slots />,
      to: "/casino/slotGame"
    },
    {
      label: t("NEW_SIDEBAR.POKER"),
      icon: <Poker />,
      to: "/casino/poker"
    },
    {
      label: t("NEW_SIDEBAR.BACCARAT"),
      icon: <Baccarat />,
      to: "/casino/baccarat"
    },
    {
      label: t("NEW_SIDEBAR.ROULETTE"),
      icon: <Roulette />,
      to: "/casino/ruleta"
    }
  ];

  const sportsItems = [
    {
      label: t("NEW_SIDEBAR.FOOTBALL"),
      icon: <Football />,
      to: { en: "/sports/Soccer", es: "/sports/Fútbol" }
    },
    {
      label: t("NEW_SIDEBAR.BASKETBALL"),
      icon: <Basketball />,
      to: { en: "/sports/Basketball", es: "sports/Baloncesto" }
    },
    {
      label: t("NEW_SIDEBAR.TENNIS"),
      icon: <Tennis />,
      to: { en: "/sports/Tennis", es: "/sports/Tenis" }
    },
    {
      label: t("NEW_SIDEBAR.AMERICAN_FOOTBALL"),
      icon: <AmericanFootball />,
      to: { en: "/sports/Football", es: "/sports/Fútbol-Americano" }
    },
    {
      label: t("NEW_SIDEBAR.MOTOR_RACING"),
      icon: <Formula1 />,
      to: { en: "/sports/Motor-Racing", es: "/sports/Automovilismo-deportivo" }
    },
    {
      label: t("NEW_SIDEBAR.BASEBALL"),
      icon: <Baseball />,
      to: { en: "/sports/Baseball", es: "/sports/Béisbol-" }
    },
    {
      label: t("NEW_SIDEBAR.E_FOOTBALL"),
      icon: <EFootball />,
      to: { en: "/sports/Virtual-Soccer", es: "/sports/Fútbol-Virtual" }
    },
    {
      label: t("NEW_SIDEBAR.MMA"),
      icon: <MMA />,
      to: "/sports/MMA"
    }
  ];

  const otherItems = [
    {
      label: t("NEW_SIDEBAR.BONUS"),
      icon: <Bonus />,
      to: "/user/bonus"
    },
    // {
    //   label: t("NEW_SIDEBAR.BLOG"),
    //   icon: <Blog style={{ fill: "#fff", width: 18, height: 18 }} />,
    //   to: "/blog",
    // },
    // {
    //   label: t("NEW_SIDEBAR.RESPONSIBLE_GAMING"),
    //   icon: <ResponsibleGaming style={{ fill: "#fff", width: 18, height: 18 }} />,
    //   to: "/responsible-gaming",
    // },
    {
      label: t("NEW_SIDEBAR.SUPPORT"),
      icon: <Support style={{ fill: "#fff", width: 18, height: 18 }} />,
      onClick: handleOpenSupportModal
    }
  ];

  const drawerPaperSx = {
    // zIndex: 9991,
    width: drawerWidth,
    backgroundColor: "#101211 !important",
    color: "#fff",
    padding: "7px 0 8px 0",
    border: "none",
    overflowX: "hidden",
     "&::-webkit-scrollbar": {
      width: "0px"
    },
    "& .menu-icon svg, & .menu-icon, & .menu-icon *": {
      transition: "fill 0.18s ease, color 0.18s ease"
    },
    ...(expanded
      ? {}
      : {
          "& .menu-item:hover .menu-icon svg, & .menu-item:hover .menu-icon, & .menu-item:hover .menu-icon *":
            {
              fill: "#FF014D !important",
              color: "#FF014D !important"
            },
          "& .menu-icon:hover svg, & .menu-icon:hover, & .menu-icon:hover *": {
            fill: "#FF014D !important",
            color: "#FF014D !important"
          },
          "& .menu-item.submenu-open .menu-icon svg, & .menu-item.submenu-open .menu-icon, & .menu-item.submenu-open .menu-icon *":
            {
              fill: "#FF014D !important",
              color: "#FF014D !important"
            }
        })
  };

  function SectionCard({ children, sx = {} }) {
    return (
      <Box
        sx={{
          borderRadius: 2.5,
          mb: 1.3,
          px: expanded ? 0.5 : 0,
          fontSize: "14px",
          whiteSpace: "nowrap",
          boxShadow: "none",
          ...sx
        }}
      >
        {children}
      </Box>
    );
  }

  function renderLanguageMenu() {
    return (
      <SectionCard sx={{ px: expanded ? 0.4 : 0 }}>
        <ListItem
          button
          onClick={undefined}
          ref={langItemRef}
          className={`menu-item ${!expanded && langHoverOpen ? "submenu-open" : ""}`}
          sx={{
            color: "#fff",
            fontWeight: 700,
            borderRadius: expanded ? "10px 10px 0 0" : 2.5,
            bgcolor: "#12494E",
            py: 1,
            fontSize: "14px",
            justifyContent: expanded ? "flex-start" : "center",
            px: expanded ? 1 : 0.5,
            "&:hover": { bgcolor: expanded ? "#2F7379" : "#0A2024" }
          }}
          onMouseEnter={(event) => {
            if (!expanded && !isTablet && !langHoverOpen) {
              cancelClose();
              setSubmenuType("");
              setSubmenuAnchor(null);
              langOpenTimerRef.current = setTimeout(() => {
                setLangHoverOpen(true);
                setLangAnchor(langItemRef.current);
              }, 100);
            }
          }}
          onMouseLeave={() => {
            if (!expanded && !isTablet) {
              cancelClose();
              setTimeout(() => {
                if (!langHoverOpen) {
                  setLangAnchor(null);
                }
              }, 180);
            }
          }}
        >
          <ListItemIcon
            className="menu-icon"
            sx={{
              minWidth: 35,
              mr: expanded ? 1 : 0,
              py: 0.4,
              px: 0.4,
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Language />
          </ListItemIcon>
          {expanded && (
            <ListItemText
              primary={t("NEW_SIDEBAR.LANGUAGE")}
              primaryTypographyProps={{
                sx: { color: "#fff", fontWeight: 700, fontSize: 14 }
              }}
            />
          )}
        </ListItem>

        {expanded ? (
          <Box
            sx={{
              bgcolor: "#0A2024",
              borderRadius: "0 0 10px 10px",
              mb: 0.7,
              py: 1,
              border: "1px solid #12494E"
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                px: 2,
                py: 1.1,
                cursor: "pointer",
                color: i18n.language === "es" ? "#FF014D" : "#BBBBBB",
                fontWeight: i18n.language === "es" ? 700 : 500,
                fontSize: "15.6px",
                borderRadius: 2.5,
                transition: "background 0.18s, color 0.18s",
                "&:hover": { background: "#12494E" }
              }}
              onClick={() => handleLangChange("es")}
            >
              <img
                src={esFlag || "/placeholder.svg"}
                alt="es"
                width={21}
                height={15}
                style={{ borderRadius: "2px" }}
              />
              <span>{t("NEW_SIDEBAR.SPANISH")}</span>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                px: 2,
                py: 1.1,
                cursor: "pointer",
                color: i18n.language === "en" ? "#FF014D" : "#BBBBBB",
                fontWeight: i18n.language === "en" ? 700 : 500,
                fontSize: "15.6px",
                borderRadius: 2.5,
                transition: "background 0.18s, color 0.18s",
                "&:hover": { background: "#12494E" }
              }}
              onClick={() => handleLangChange("en")}
            >
              <img
                src={enFlag || "/placeholder.svg"}
                alt="en"
                width={21}
                height={15}
                style={{ borderRadius: "2px" }}
              />
              <span>{t("NEW_SIDEBAR.ENGLISH")}</span>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                px: 2,
                py: 1.1,
                cursor: "pointer",
                color: i18n.language === "pt" ? "#FF014D" : "#BBBBBB",
                fontWeight: i18n.language === "pt" ? 700 : 500,
                fontSize: "15.6px",
                borderRadius: 2.5,
                transition: "background 0.18s, color 0.18s",
                "&:hover": { background: "#12494E" }
              }}
              onClick={() => handleLangChange("pt")}
            >
              <img
                src={pgFlag || "/placeholder.svg"}
                alt="pt"
                width={21}
                height={15}
                style={{ borderRadius: "2px" }}
              />
              <span>{t("NEW_SIDEBAR.PORTUGUESE")}</span>
            </Box>
          </Box>
        ) : (
          <Popover
            open={Boolean(langAnchor) && langHoverOpen}
            anchorEl={langAnchor}
            onClose={() => {
              setLangHoverOpen(false);
              handleLangClose();
            }}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center"
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left"
            }}
            disableRestoreFocus
            sx={{
              pointerEvents: "none",
              ml: 6,
              mt: 25,
              zIndex: 1300
            }}
            PaperProps={{
              sx: {
                background: "#0A2024",
                color: "#fff",
                borderRadius: 2.5,
                cursor: "pointer",
                py: 0,
                px: 0,
                boxShadow: "0 1px 3px rgba(14,17,39,0.08)",
                pointerEvents: "auto"
              },
              onMouseEnter: () => {
                setLangHoverOpen(true);
              },
              onMouseLeave: () => {
                setTimeout(() => {
                  setLangHoverOpen(false);
                  handleLangClose();
                }, 180);
              }
            }}
          >
            <Box
              sx={{
                width: 160,
                pointerEvents: "auto",
                background: "#0A2024",
                borderRadius: 2.5,
                py: 1
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  px: 2,
                  py: 1.1,
                  cursor: "pointer",
                  color: i18n.language === "es" ? "#FF014D" : "#BBBBBB",
                  fontWeight: i18n.language === "es" ? 700 : 500,
                  fontSize: "15.6px",
                  borderRadius: 2.5,
                  transition: "background 0.18s, color 0.18s",
                  "&:hover": { background: "#12494E" }
                }}
                onClick={() => handleLangChange("es")}
              >
                <img
                  src={esFlag || "/placeholder.svg"}
                  alt="es"
                  width={21}
                  height={15}
                  style={{ borderRadius: "2px" }}
                />
                <span>{t("NEW_SIDEBAR.SPANISH")}</span>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  px: 2,
                  py: 1.1,
                  cursor: "pointer",
                  color: i18n.language === "en" ? "#FF014D" : "#BBBBBB",
                  fontWeight: i18n.language === "en" ? 700 : 500,
                  fontSize: "15.6px",
                  borderRadius: 2.5,
                  transition: "background 0.18s, color 0.18s",
                  "&:hover": { background: "#12494E" }
                }}
                onClick={() => handleLangChange("en")}
              >
                <img
                  src={enFlag || "/placeholder.svg"}
                  alt="en"
                  width={21}
                  height={15}
                  style={{ borderRadius: "2px" }}
                />
                <span>{t("NEW_SIDEBAR.ENGLISH")}</span>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  px: 2,
                  py: 1.1,
                  cursor: "pointer",
                  color: i18n.language === "pt" ? "#FF014D" : "#BBBBBB",
                  fontWeight: i18n.language === "pt" ? 700 : 500,
                  fontSize: "15.6px",
                  borderRadius: 2.5,
                  transition: "background 0.18s, color 0.18s",
                  "&:hover": { background: "#12494E" }
                }}
                onClick={() => handleLangChange("pt")}
              >
                <img
                  src={pgFlag || "/placeholder.svg"}
                  alt="pt"
                  width={21}
                  height={15}
                  style={{ borderRadius: "2px" }}
                />
                <span>{t("NEW_SIDEBAR.PORTUGUESE")}</span>
              </Box>
            </Box>
          </Popover>
        )}
      </SectionCard>
    );
  }

  const handleDesktopHoverOpen = (type) => (event) => {
    if (!isTablet && !expanded && submenuType !== type) {
      cancelClose();
      setLangHoverOpen(false);
      setLangAnchor(null);
      openTimerRef.current = setTimeout(() => {
        setSubmenuType(type);
        if (type === "casino") {
          setSubmenuAnchor(casinoIconRef.current);
        } else if (type === "sports") {
          setSubmenuAnchor(sportsIconRef.current);
        }
        lastAnchorRef.current = event.currentTarget;
      }, 100);
    }
  };

  const handleOtherItemHover = () => {
    if (!expanded && !isTablet) {
      cancelClose();
      setSubmenuType("");
      setSubmenuAnchor(null);
      setLangHoverOpen(false);
      setLangAnchor(null);
    }
  };

  function renderSubmenu() {
    let items = [];
    if (submenuType === "casino") items = casinoItems;
    else if (submenuType === "sports") items = sportsItems;
    else return null;

    return (
      <Box
        sx={{ pointerEvents: "auto" }}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <List sx={{ background: "#0A2024", minWidth: 220, py: 1 }}>
          {items.map((item) => (
            <NavLink
              key={item.label}
              to={resolveTo(item.to)}
              style={linkStyles}
              onClick={closeSubmenuNow}
            >
              <ListItem
                button
                className="menu-item"
                sx={{
                  "&:hover": { bgcolor: "#12494E" },
                  borderRadius: 2.5
                }}
              >
                <ListItemIcon
                  className="menu-icon"
                  sx={{
                    minWidth: 35
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: { fontSize: 14, fontWeight: 700 }
                  }}
                />
              </ListItem>
            </NavLink>
          ))}
        </List>
      </Box>
    );
  }

  return (
    <>
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": drawerPaperSx
        }}
        PaperProps={{
          style: { minHeight: "100vh", overflowX: "hidden" }
        }}
      >
        <Box
          sx={{
            mb: 1.6,
            ml: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: expanded ? "space-between" : "center"
          }}
        >
          <IconButton
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ padding: 0 }}
            style={{ display: isTablet ? "none" : "inline-flex" }}
          >
            <ArrowToggleIcon expanded={!expanded} />
          </IconButton>
        </Box>

        <List
          sx={{
            pr: 0.3,
            bgcolor: !expanded ? "#0A2024" : "#0A2024",
            width: drawerWidth - 15,
            borderRadius: !expanded && "10px",
            py: !expanded ? 1 : 0,
            px: !expanded ? 0.5 : 0,
            mr: !expanded ? 0.5 : 0.5,
            ml: !expanded ? 1.5 : 1,
            "& .MuiListItem-root": {
              fontWeight: 700,
              fontSize: "14px"
            },
            "& .MuiTypography-root": {
              fontSize: "14px",
              fontWeight: 700
            }
          }}
        >
          <SectionCard>
            <ListItem
              button
              className={`menu-item ${submenuType === "casino" ? "submenu-open" : ""}`}
              onClick={
                isTablet
                  ? (e) => handleSubmenuOpen("casino", e.currentTarget)
                  : () => setOpenCasino((p) => !p)
              }
              onMouseEnter={handleDesktopHoverOpen("casino")}
              onMouseLeave={() => {
                if (!expanded && !isTablet) {
                  scheduleClose();
                }
              }}
              sx={{
                fontWeight: 700,
                borderRadius: openCasino && expanded ? "10px 10px 0 0" : 2.5,
                bgcolor: "#12494E",
                py: 1,
                minHeight: 42,
                fontSize: "14px",
                cursor: "pointer",
                justifyContent: expanded ? "flex-start" : "center",
                px: expanded ? 1 : 0.5,
                "&:hover": { bgcolor: expanded ? "#2F7379" : "#0A2024" }
              }}
            >
              <ListItemIcon
                ref={casinoIconRef}
                className="menu-icon"
                sx={{
                  minWidth: 35,
                  mr: expanded ? 1 : 0,
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <CasinoIcon />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary={t("NEW_SIDEBAR.CASINO")}
                  primaryTypographyProps={{
                    sx: { fontSize: 14, fontWeight: 700 }
                  }}
                />
              )}
              {expanded && (
                <Frame
                  open={openCasino}
                  style={{
                    // transform: openCasino ? "rotate(-180deg)" : "rotate(0deg)",
                    transform: openCasino ? "rotate(0deg)" : "rotate(-180deg)",
                    transformOrigin: "11px 11px"
                  }}
                />
              )}
            </ListItem>

            <Popover
              open={
                !!submenuAnchor &&
                submenuType === "casino" &&
                (!expanded || isTablet)
              }
              anchorEl={submenuAnchor || lastAnchorRef.current}
              onClose={handleSubmenuClose}
              anchorOrigin={{
                vertical: "center",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "center",
                horizontal: "left"
              }}
              disableRestoreFocus
              sx={{
                pointerEvents: "none",
                zIndex: 1300,
                ml: 6,
                mt: 5
              }}
              PaperProps={{
                sx: {
                  background: "#0A2024",
                  color: "#fff",
                  pointerEvents: "auto",
                  borderRadius: 2.5
                },
                onMouseEnter: cancelClose,
                onMouseLeave: () => {
                  if (!expanded && !isTablet) {
                    scheduleClose();
                  }
                }
              }}
            >
              {renderSubmenu()}
            </Popover>

            {!isTablet && expanded && (
              <Collapse in={openCasino && expanded}>
                <List
                  component="div"
                  disablePadding
                  sx={{
                    border: openCasino && "1px solid #0A2024",
                    borderTop: "none",
                    borderRadius: "0 0 10px 10px"
                  }}
                >
                  {casinoItems.map((item) => (
                    <NavLink
                      key={item.label}
                      to={resolveTo(item.to)}
                      style={linkStyles}
                    >
                      <ListItem
                        button
                        className="menu-item"
                        sx={{ "&:hover": { bgcolor: "#12494E" } }}
                      >
                        <ListItemIcon
                          className="menu-icon"
                          sx={{
                            minWidth: 35,
                            mr: 0.2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "& svg": {
                              width: 18,
                              height: 18,
                              fill: "#fff !important",
                              display: "block",
                              opacity: 1,
                              visibility: "visible"
                            }
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            sx: { fontSize: 14, fontWeight: 700 }
                          }}
                        />
                      </ListItem>
                    </NavLink>
                  ))}
                </List>
              </Collapse>
            )}
          </SectionCard>

          <SectionCard>
            <ListItem
              button
              onClick={
                isTablet
                  ? (e) => handleSubmenuOpen("sports", e.currentTarget)
                  : () => setOpenSports((p) => !p)
              }
              onMouseEnter={handleDesktopHoverOpen("sports")}
              onMouseLeave={() => {
                if (!expanded && !isTablet) {
                  scheduleClose();
                }
              }}
              className={`menu-item ${submenuType === "sports" ? "submenu-open" : ""}`}
              sx={{
                fontWeight: 700,
                borderRadius: openSports && expanded ? "10px 10px 0 0" : 2.5,
                bgcolor: "#12494E",
                py: 1,
                minHeight: 42,
                fontSize: "14px",
                cursor: "pointer",
                justifyContent: expanded ? "flex-start" : "center",
                px: expanded ? 1 : 0,
                "&:hover": { bgcolor: expanded ? "#12494E" : "#0A2024" }
              }}
            >
              <ListItemIcon
                ref={sportsIconRef}
                className="menu-icon"
                sx={{
                  minWidth: 35,
                  mr: expanded ? 1 : 0,
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <Sports />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary={t("NEW_SIDEBAR.SPORTS")}
                  primaryTypographyProps={{
                    sx: { fontSize: 14, fontWeight: 700 }
                  }}
                />
              )}
              {expanded && (
                <Frame
                  open={!openSports}
                  style={{
                    // transform: openSports ? "rotate(-180deg)" : "rotate(0deg)",
                    transform: openSports ? "rotate(0deg)" : "rotate(-180deg)",
                    transformOrigin: "11px 11px",
                    transition: "transform 0.45s ease-in-out"
                  }}
                />
              )}
            </ListItem>

            <Popover
              open={
                !!submenuAnchor &&
                submenuType === "sports" &&
                (!expanded || isTablet)
              }
              anchorEl={submenuAnchor || lastAnchorRef.current}
              onClose={handleSubmenuClose}
              anchorOrigin={{
                vertical: "center",
                horizontal: "center"
              }}
              transformOrigin={{
                vertical: "center",
                horizontal: "left"
              }}
              disableRestoreFocus
              sx={{
                pointerEvents: "none",
                zIndex: 1300,
                ml: 6,
                mt: 12
              }}
              PaperProps={{
                sx: {
                  background: "#0A2024",
                  color: "#fff",
                  pointerEvents: "auto",
                  borderRadius: 2.5
                },
                onMouseEnter: cancelClose,
                onMouseLeave: () => {
                  if (!expanded && !isTablet) {
                    scheduleClose();
                  }
                }
              }}
            >
              {renderSubmenu()}
            </Popover>

            {!isTablet && expanded && (
              <Collapse
                in={openSports && expanded}
                sx={{
                  border: openSports && "1px solid #0A2024",
                  borderTop: "none",
                  borderRadius: "0 0 10px 10px"
                }}
              >
                <List component="div" disablePadding>
                  {sportsItems.map((item) => (
                    <NavLink
                      key={item.label}
                      to={resolveTo(item.to)}
                      style={linkStyles}
                    >
                      <ListItem
                        button
                        className="menu-item"
                        sx={{ "&:hover": { bgcolor: "#12494E" } }}
                      >
                        <ListItemIcon
                          className="menu-icon"
                          sx={{
                            minWidth: 35,
                            mr: 0.2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "& svg": {
                              width: 18,
                              height: 18,
                              fill: "#fff !important",
                              display: "block",
                              opacity: 1,
                              visibility: "visible"
                            }
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            sx: { fontSize: 14, fontWeight: 700 }
                          }}
                        />
                      </ListItem>
                    </NavLink>
                  ))}
                </List>
              </Collapse>
            )}
          </SectionCard>

          {otherItems.map((item) => (
            <SectionCard key={item.label}>
              {item.onClick ? (
                <ListItem
                  button
                  onClick={item.onClick}
                  className="menu-item"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    borderRadius: 2.5,
                    bgcolor: "#12494E",
                    py: 1,
                    minHeight: 42,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    justifyContent: expanded ? "flex-start" : "center",
                    px: expanded ? 1 : 0,
                    "&:hover": { bgcolor: expanded ? "#12494E" : "#0A2024" }
                  }}
                  onMouseEnter={handleOtherItemHover}
                  onMouseLeave={() => {
                    if (!expanded && !isTablet) {
                      scheduleClose();
                    }
                  }}
                >
                  <ListItemIcon
                    className="menu-icon"
                    sx={{
                      minWidth: 35,
                      mr: expanded ? 1 : 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "& svg": {
                        width: 18,
                        height: 18,
                        fill: "#fff !important",
                        display: "block",
                        opacity: 1,
                        visibility: "visible"
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {expanded && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: { fontSize: 14, fontWeight: 700 }
                      }}
                    />
                  )}
                </ListItem>
              ) : (
                <NavLink to={resolveTo(item.to)} style={linkStyles}>
                  <ListItem
                    button
                    className="menu-item"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      borderRadius: 2.5,
                      bgcolor: "#12494E",
                      py: 1,
                      minHeight: 42,
                      fontSize: "14px",
                      justifyContent: expanded ? "flex-start" : "center",
                      px: expanded ? 1 : 0,
                      "&:hover": { bgcolor: expanded ? "#12494E" : "#0A2024" }
                    }}
                    onMouseEnter={handleOtherItemHover}
                    onMouseLeave={() => {
                      if (!expanded && !isTablet) {
                        scheduleClose();
                      }
                    }}
                  >
                    <ListItemIcon
                      className="menu-icon"
                      sx={{
                        minWidth: 35,
                        mr: expanded ? 1 : 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        "& svg": {
                          width: 18,
                          height: 18,
                          fill: "#fff !important",
                          display: "block",
                          opacity: 1,
                          visibility: "visible"
                        }
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {expanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          sx: { fontSize: 14, fontWeight: 700 }
                        }}
                      />
                    )}
                  </ListItem>
                </NavLink>
              )}
            </SectionCard>
          ))}

          {renderLanguageMenu()}
        </List>
      </Drawer>
      <Modal
        isShowing={openSupportModal}
        maxWidth={"600px"}
        maxHeight={"90%"}
        content={
          <DialogContent>
            <SupportModal setModalopen={setOpenSupportModal} />
          </DialogContent>
        }
        hide={() => setOpenSupportModal(false)}
      />
    </>
  );
}

export default Sidebar;
